import 'src/index.css';

import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { ThemeProvider } from '@onevision-resources/design-theme';
import { OvLogin } from '@onevision-resources/login';
import { GraphQLProvider } from '@provision/graphql-client';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { BrowserRouter } from 'react-router-dom';
import {
  AppSettings,
  AppSnackbar,
  BulkUpdateGlobalBanner,
  ConfirmationModal,
  GlobalErrorBoundary,
  MetaTags,
  OVPostHogProvider,
  PostHogPageviewTracker,
  QuickActionsProvider,
  StatusBarStyleUpdater,
} from 'src/components';
import { useThemeSwitch } from 'src/components/Navbar/use-theme-switch';
import { WebSocketConnection } from 'src/core/realtime-connection';
import { useGraphQLClientConfig } from 'src/graphql/setup/config';
import { theme } from 'src/utils/theme';
import { SelectedEntitySync } from './core/selected-entity-sync';
import {
  useFetchCurrentUserProfile,
  useFetchPartnerGroupRules,
  useFetchPartners,
  useFetchUserGroupRuleValues,
} from './graphql';
import { AppRoutes } from './routes';
import { useGlobalStore } from './store';

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

const GlobalPreparer = () => {
  useFetchCurrentUserProfile();
  useFetchPartnerGroupRules();
  useFetchUserGroupRuleValues();
  useFetchPartners();

  return null;
};

const App = () => {
  const { config } = useGraphQLClientConfig();
  const { forceThemeMode } = useThemeSwitch();
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();

  return (
    <GraphQLProvider config={config} selectedPartnerId={selectedPartnerId}>
      <OVPostHogProvider>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <BrowserRouter>
            <PostHogPageviewTracker />
            <ThemeProvider theme={theme} forcedMode={forceThemeMode ?? undefined}>
              <StatusBarStyleUpdater />
              <GlobalErrorBoundary>
                <AppSettings />
                <GlobalPreparer />
                <QuickActionsProvider>
                  <MetaTags title="ProVision">
                    <AppRoutes />
                  </MetaTags>
                </QuickActionsProvider>
                <AppSnackbar />
                <BulkUpdateGlobalBanner />
                <ConfirmationModal />
                <WebSocketConnection />
                <SelectedEntitySync />
              </GlobalErrorBoundary>
            </ThemeProvider>
          </BrowserRouter>
        </LocalizationProvider>
      </OVPostHogProvider>
    </GraphQLProvider>
  );
};

const AppWithAuth = () => {
  return <OvLogin app={App} logo="/portal-logo.svg" withSignUp />;
};

export default AppWithAuth;
